import React, { useState, useEffect } from 'react';
import {
  A2UIText,
  A2UIButton,
  A2UIInput,
  A2UIList,
  A2UICard,
  A2UICarousel,
  A2UIForm,
  A2UIContainer,
  A2UISelect,
  A2UICheckbox,
  A2UIDateInput
} from './A2UIComponents';

/**
 * A2UI React Renderer
 * Renders A2UI widgets according to A2UI specification
 * 
 * A2UI Format:
 * {
 *   "widgets": [
 *     {
 *       "id": "widget_1",
 *       "type": "list" | "card" | "carousel" | "button" | "form" | "text" | "input" | "container",
 *       "props": { ... },
 *       "children": [ ... ] // optional, for nested widgets
 *     }
 *   ]
 * }
 */


// A2UIComponents (generate event)
//     ↓ onClick/onSubmit/onChange
// A2UIRenderer.handleEvent (46-49 lines)
//     ↓ onEvent(event)
// WidgetBlocks (15 lines)
//     ↓ onEvent={onEvent}
// Message.handleA2UIEvent (8-20 lines)
//     ↓ onA2UIEvent(event, message)
// App.js.handleA2UIEvent (283 lines)


const A2UIRenderer = ({ widgets, onEvent, className, style }) => {
  const [widgetState, setWidgetState] = useState({});

  // Initialize widget state
  useEffect(() => {
    if (widgets && Array.isArray(widgets)) {
      const initialState = {};
      widgets.forEach(widget => {
        if (widget.id) {
          // Initialize with props, and extract value/checked if present
          initialState[widget.id] = {
            ...widget.props,
            value: widget.props?.value || widget.props?.defaultValue || '',
            checked: widget.props?.checked || false
          };
        }
      });
      setWidgetState(initialState);
    }
  }, [widgets]);

  const handleEvent = (event) => {
    // Update widget state for input changes
    if (event.type === 'input_change' && event.widgetId) {
      setWidgetState(prev => ({
        ...prev,
        [event.widgetId]: {
          ...prev[event.widgetId],
          value: event.value,
          checked: typeof event.value === 'boolean' ? event.value : prev[event.widgetId]?.checked
        }
      }));
    }
    
    if (onEvent) {
      onEvent(event);
    }
  };

  const renderWidget = (widget) => {
    if (!widget || !widget.type) {
      return null;
    }

    const widgetId = widget.id || `widget_${Math.random()}`;
    // Support both formats: with props (old) and without props (new flat format)
    // Priority: use widget properties directly, fallback to widget.props for backward compatibility
    const { id, type, children, props: widgetPropsFromNested, ...widgetDirectProps } = widget;
    // Merge: direct properties first, then nested props (if exists), then add id
    const props = { 
      ...widgetDirectProps,  // New flat format: properties directly on widget
      ...(widgetPropsFromNested || {}),  // Old format: properties in props object (backward compatibility)
      id: widgetId 
    };
    const widgetChildren = children || widget.children || [];

    switch (widget.type.toLowerCase()) {
      case 'text':
        return (
          <A2UIText
            key={widgetId}
            text={props.text || ''}
            style={props.style}
            className={props.className}
          />
        );

      case 'button':
        // Extract amount and currency from props or from data object
        // Backend format: amount and currency can be in data object: { "data": { "amount": 123, "currency": "USD" } }
        // Or directly on component: { "amount": 123, "currency": "USD" }
        const buttonAmount = props.amount || (props.data && props.data.amount) || null;
        const buttonCurrency = props.currency || (props.data && props.data.currency) || null;
        
        return (
          <A2UIButton
            key={widgetId}
            label={props.label || ''}
            onClick={handleEvent}
            variant={props.variant}
            disabled={props.disabled}
            style={props.style}
            className={props.className}
            action={props.action || null}
            data={props.data || null}
            amount={buttonAmount}
            currency={buttonCurrency}
            id={widgetId}
          />
        );

      case 'input':
        // Use inputType if available, fallback to type for backward compatibility
        const inputType = props.inputType || props.type || 'text';
        
        // If inputType is "select" and has options, render Select component instead
        if (inputType === 'select' && props.options && Array.isArray(props.options) && props.options.length > 0) {
          return (
            <A2UISelect
              key={widgetId}
              label={props.label}
              value={widgetState[widgetId]?.value !== undefined ? widgetState[widgetId].value : (props.value || props.defaultValue || '')}
              options={props.options}
              required={props.required}
              onChange={handleEvent}
              style={props.style}
              className={props.className}
              id={widgetId}
            />
          );
        }
        
        return (
          <A2UIInput
            key={widgetId}
            type={inputType}
            placeholder={props.placeholder}
            label={props.label}
            required={props.required}
            value={widgetState[widgetId]?.value !== undefined ? widgetState[widgetId].value : (props.value || props.defaultValue || '')}
            onChange={handleEvent}
            style={props.style}
            className={props.className}
            id={widgetId}
            min={props.min}
            max={props.max}
            step={props.step}
          />
        );

      case 'select':
      case 'dropdown':
        return (
          <A2UISelect
            key={widgetId}
            label={props.label}
            value={widgetState[widgetId]?.value !== undefined ? widgetState[widgetId].value : (props.value || props.defaultValue || '')}
            options={props.options || []}
            required={props.required}
            onChange={handleEvent}
            style={props.style}
            className={props.className}
            id={widgetId}
          />
        );

      case 'checkbox':
        return (
          <A2UICheckbox
            key={widgetId}
            label={props.label}
            checked={widgetState[widgetId]?.checked || widgetState[widgetId]?.value || false}
            onChange={handleEvent}
            style={props.style}
            className={props.className}
            id={widgetId}
          />
        );

      case 'date':
      case 'dateinput':
        return (
          <A2UIDateInput
            key={widgetId}
            label={props.label}
            value={widgetState[widgetId]?.value !== undefined ? widgetState[widgetId].value : (props.value || props.defaultValue || '')}
            min={props.min}
            max={props.max}
            required={props.required}
            onChange={handleEvent}
            style={props.style}
            className={props.className}
            id={widgetId}
          />
        );

      case 'number':
        return (
          <A2UIInput
            key={widgetId}
            type="number"
            placeholder={props.placeholder}
            label={props.label}
            required={props.required}
            value={widgetState[widgetId]?.value !== undefined ? widgetState[widgetId].value : (props.value || props.defaultValue || '')}
            onChange={handleEvent}
            style={props.style}
            className={props.className}
            id={widgetId}
            min={props.min}
            max={props.max}
            step={props.step}
          />
        );

      case 'list':
        return (
          <A2UIList
            key={widgetId}
            title={props.title}
            items={props.items || []}
            onItemClick={props.onItemClick !== false ? handleEvent : undefined}
            style={props.style}
            className={props.className}
            id={widgetId}
          />
        );

      case 'card':
      case 'cards': // Keep for backward compatibility
        // If Card has direct properties (title, subtitle, price) but no items, convert to single-item array
        // This handles single Card components vs Card Container (with items array)
        let cardItems = props.items || [];
        if (cardItems.length === 0 && (props.title || props.subtitle || props.price != null)) {
          // Single card with direct properties - convert to items array format
          cardItems = [{
            id: widgetId,
            title: props.title,
            subtitle: props.subtitle,
            price: props.price,
            currency: props.currency,
            image_url: props.image_url || props.image || props.imageUrl,
            link: props.link,
            left_text1: props.left_text1,
            middle_text: props.middle_text,
            right_text1: props.right_text1,
            left_text2: props.left_text2,
            right_text2: props.right_text2,
            tags: props.tags,
            actions: props.actions
          }];
        }
        
        // Debug log for troubleshooting
        if (process.env.NODE_ENV === 'development' && cardItems.length === 0) {
          console.warn(`⚠️ Card ${widgetId} has no items and no direct properties:`, props);
        }
        
        return (
          <A2UICard
            key={widgetId}
            title={cardItems.length > 1 ? props.title : undefined} // Only show title if multiple items
            items={cardItems}
            onCardClick={props.onCardClick !== false ? handleEvent : undefined}
            style={props.style}
            className={props.className}
            id={widgetId}
          />
        );

      case 'carousel':
        return (
          <A2UICarousel
            key={widgetId}
            title={props.title}
            items={props.items || []}
            onCardClick={props.onCardClick !== false ? handleEvent : undefined}
            style={props.style}
            className={props.className}
            id={widgetId}
          />
        );

      case 'form':
        return (
          <A2UIForm
            key={widgetId}
            title={props.title}
            fields={props.fields || []}
            onSubmit={handleEvent}
            submitLabel={props.submitLabel}
            style={props.style}
            className={props.className}
            id={widgetId}
          >
            {widgetChildren.map(child => renderWidget(child))}
          </A2UIForm>
        );

      case 'container':
        return (
          <A2UIContainer
            key={widgetId}
            style={props.style}
            className={props.className}
            id={widgetId}
          >
            {widgetChildren.map(child => renderWidget(child))}
          </A2UIContainer>
        );

      default:
        console.warn(`Unknown A2UI widget type: ${widget.type}`);
        return null;
    }
  };

  if (!widgets || !Array.isArray(widgets) || widgets.length === 0) {
    return null;
  }

  return (
    <div className={`a2ui-renderer ${className || ''}`} style={style}>
      {widgets.map(widget => renderWidget(widget))}
    </div>
  );
};

export default A2UIRenderer;

